import React, { useState, useCallback, useMemo } from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Tooltip from '@amzn/meridian/tooltip';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';
import HoverZoomImage from './HoverZoomImage';
import Icon from '@amzn/meridian/icon';
import closeKnockoutTokens from '@amzn/meridian-tokens/base/icon/close-knockout';
import DatePicker from '@amzn/meridian/date-picker';
import { storeSubcategory } from '@components/agile/types/AgileMacroplanogramTypes';
import {
  timestampToDateStr,
  dateStrToTimestamp,
  isIndefinite,
  isActiveEntry,
  getTodayStr,
  isBeforeDate,
  getEarliestStartDateStr,
  isOutsideDateRange,
} from '@components/agile/utils/dateHelpers';
import getDateInUTC from '@components/agile/utils/GetDateUtils';
import { ItemEntry, ItemEntryInput } from '@graphql/generated/graphql';
import useApplyItemsChange from '@store/hooks/useApplyItemsChange';
import AddItemInput, {
  StagedItem,
  isSaveEnabled as isAddSaveEnabled,
  buildMutationInput,
  mapSaveErrors,
} from './AddItemInput';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import { MacroplanogramScope } from 'src/types/programConfig';

/** Local editable copy of an entry row. */
interface EditableEntry {
  entryId: string;
  optimisticLockId: string | undefined;
  itemId: string;
  itemType: ItemEntry['itemType'];
  subcategoryId: string | null | undefined;
  categoryId: string | null | undefined;
  startTime: number;
  endTime: number;
  title: string | undefined;
  image: string | undefined;
}

export interface ModifyItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onModifySuccess: () => void;
  storeId: string;
  macroplanogramId: string;
  selectedEntries: ItemEntry[];
  subcategories: storeSubcategory[];
  selectedDate: string;
  macroplanogramStartTime: number;
  macroplanogramEndTime: number;
  existingItems: ItemEntry[] | undefined;
  /** Macroplanogram-level categoryId (Phase 1 GFC only). When set, save uses
   *  it as the scope identifier instead of per-item subcategoryId. */
  categoryId?: string | null;
}

/** Convert an ItemEntry from the table data into a local editable row. */
function toEditableEntry(entry: ItemEntry): EditableEntry {
  return {
    entryId: entry.entryId,
    optimisticLockId: entry.optimisticLockId != null ? String(entry.optimisticLockId) : undefined,
    itemId: entry.itemId,
    itemType: entry.itemType,
    subcategoryId: entry.subcategoryId,
    categoryId: entry.categoryId,
    startTime: Number(entry.startTime),
    endTime: Number(entry.endTime),
    title: entry.itemMetaDataEntry?.title ?? undefined,
    image: entry.itemMetaDataEntry?.image ?? undefined,
  };
}

const ModifyItemsModal = (props: ModifyItemsModalProps) => {
  const {
    isOpen,
    onClose,
    onModifySuccess,
    storeId,
    macroplanogramId,
    selectedEntries,
    subcategories,
    selectedDate,
    macroplanogramStartTime,
    macroplanogramEndTime,
    existingItems,
    categoryId,
  } = props;

  const config = useProgramConfig();
  const itemIdentifierLabel = config.labels.itemIdentifierType;
  const subcategoryLabel = config.labels.subcategory;
  const categoryLabel = config.labels.category;
  const scope: MacroplanogramScope = config.features.itemAssortment?.scope ?? 'subcategory';
  const scopeColumnLabel = scope === 'subcategory' ? subcategoryLabel : categoryLabel;

  // Memoize subcategory lookup map for O(1) lookups in the table
  const subcategoryMap = useMemo(
    () => new Map(subcategories.map((s) => [s.subcategoryId, s.subcategoryName])),
    [subcategories],
  );

  // Local editable state — initialised from selectedEntries each time the modal opens
  const [editableEntries, setEditableEntries] = useState<EditableEntry[]>([]);
  const [saveErrors, setSaveErrors] = useState<Map<string, string>>(new Map());
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Add-new-items section state
  const [showAddSection, setShowAddSection] = useState<boolean>(false);
  const [newItems, setNewItems] = useState<StagedItem[]>([]);

  const { mutateAsync, isLoading: isSaving } = useApplyItemsChange();

  // Keep a snapshot of the original values so we can detect changes
  const [originalEntries, setOriginalEntries] = useState<EditableEntry[]>([]);

  // Re-initialise editable state whenever the modal opens with new entries
  React.useEffect(() => {
    if (isOpen && selectedEntries.length > 0) {
      const entries = selectedEntries.map(toEditableEntry);
      setEditableEntries(entries);
      setOriginalEntries(entries);
      setSaveErrors(new Map());
      setGlobalError(null);
      setShowAddSection(false);
      setNewItems([]);
    }
  }, [isOpen, selectedEntries]);

  const handleClose = useCallback(() => {
    setEditableEntries([]);
    setOriginalEntries([]);
    setSaveErrors(new Map());
    setGlobalError(null);
    setShowAddSection(false);
    setNewItems([]);
    onClose();
  }, [onClose]);

  // --- Field change handlers ---

  const handleEndDateChange = useCallback((entryId: string, dateStr: string) => {
    const newEndTime = dateStrToTimestamp(dateStr);
    setEditableEntries((prev) => prev.map((e) => (e.entryId === entryId ? { ...e, endTime: newEndTime } : e)));
  }, []);

  const handleStartDateChange = useCallback((entryId: string, dateStr: string) => {
    const newStartTime = dateStrToTimestamp(dateStr);
    setEditableEntries((prev) => prev.map((e) => (e.entryId === entryId ? { ...e, startTime: newStartTime } : e)));
  }, []);

  const handleRemoveEntry = useCallback((entryId: string) => {
    setEditableEntries((prev) => prev.filter((e) => e.entryId !== entryId));
    setOriginalEntries((prev) => prev.filter((e) => e.entryId !== entryId));
  }, []);

  // --- Detect which entries have been modified ---

  const changedEntries = useMemo(() => {
    const originalMap = new Map(originalEntries.map((o) => [o.entryId, o]));
    return editableEntries.filter((edited) => {
      const original = originalMap.get(edited.entryId);
      if (!original) return false;
      return edited.endTime !== original.endTime || edited.startTime !== original.startTime;
    });
  }, [editableEntries, originalEntries]);

  const hasModifications = changedEntries.length > 0;
  const hasNewItems = isAddSaveEnabled(newItems, scope === 'subcategory');
  const newItemsMissingSubcategory = newItems.length > 0 && !hasNewItems;
  const saveEnabled = (hasModifications || hasNewItems) && !newItemsMissingSubcategory && !isSaving;

  // --- Save handler ---

  const handleSave = useCallback(async () => {
    setGlobalError(null);
    setSaveErrors(new Map());

    // Check for cross-list overlap: same itemId + subcategoryId in both update and add lists
    // Allows same itemId with different subcategories (e.g., moving item between subcategories)
    if (hasNewItems && changedEntries.length > 0) {
      const compositeKey = (itemId: string, subcategoryId?: string | null) => `${itemId}:${subcategoryId ?? ''}`;
      const updateKeys = new Set(changedEntries.map((e) => compositeKey(e.itemId, e.subcategoryId)));
      const overlap = newItems.filter((item) => updateKeys.has(compositeKey(item.itemId, item.subcategoryId)));
      if (overlap.length > 0) {
        setGlobalError(
          `Cannot modify and add the same item(s) with the same subcategory in one operation: ${overlap.map((i) => i.itemId).join(', ')}. Please remove duplicates from the add section.`,
        );
        return;
      }
    }

    const updateEntries: ItemEntryInput[] = changedEntries.map((entry) => ({
      entryId: entry.entryId,
      optimisticLockId: entry.optimisticLockId,
      itemId: entry.itemId,
      itemType: entry.itemType,
      subcategoryId: entry.subcategoryId,
      categoryId: entry.categoryId ?? categoryId,
      startTime: entry.startTime,
      endTime: entry.endTime,
    }));

    // Build newEntries from the add section if present
    const newEntries = hasNewItems
      ? buildMutationInput(newItems, storeId, macroplanogramId, categoryId ?? undefined).newEntries
      : undefined;

    try {
      const result = await mutateAsync({
        input: {
          storeId,
          macroplanogramId,
          updateEntries: updateEntries.length > 0 ? updateEntries : undefined,
          newEntries,
        },
      });
      const errors = result?.applyItemsChange?.errors ?? [];

      if (errors.length > 0) {
        setSaveErrors(mapSaveErrors(errors, subcategoryMap));
      } else {
        onModifySuccess();
        handleClose();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred while saving changes.';
      setGlobalError(message);
    }
  }, [
    changedEntries,
    newItems,
    hasNewItems,
    storeId,
    macroplanogramId,
    subcategoryMap,
    mutateAsync,
    onModifySuccess,
    handleClose,
    categoryId,
  ]);

  return (
    <Modal title="Modify Items" open={isOpen} onClose={handleClose} scrollContainer="modal">
      <Column spacing="400">
        {globalError && (
          <Alert type="error" size="medium">
            {globalError}
          </Alert>
        )}

        {editableEntries.length === 0 ? (
          <Text type="b300">No items selected.</Text>
        ) : (
          <Table headerRows={1} showDividers spacing="small">
            <TableRow backgroundColor="secondary">
              <TableCell>{itemIdentifierLabel}</TableCell>
              <TableCell>Image</TableCell>
              <TableCell>Title</TableCell>
              {scope === 'subcategory' && <TableCell>{scopeColumnLabel}</TableCell>}
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Remove</TableCell>
            </TableRow>
            {editableEntries.map((entry) => (
              <TableRow key={entry.entryId}>
                <TableCell>
                  <Column>
                    <Text type="b200">{entry.itemId}</Text>
                    {saveErrors.has(entry.itemId) && changedEntries.some((c) => c.entryId === entry.entryId) && (
                      <Text type="b100" color="error">
                        {saveErrors.get(entry.itemId)}
                      </Text>
                    )}
                  </Column>
                </TableCell>
                <TableCell>
                  {entry.image ? (
                    <HoverZoomImage src={entry.image} alt={entry.itemId} width={40} height={40} />
                  ) : (
                    <Text type="b200">—</Text>
                  )}
                </TableCell>
                <TableCell>
                  <Box maxWidth="200px">
                    <Text type="b200">{entry.title ?? '—'}</Text>
                  </Box>
                </TableCell>
                {scope === 'subcategory' ? (
                  <TableCell>
                    <Text type="b200">{subcategoryMap.get(entry.subcategoryId ?? '') ?? ''}</Text>
                  </TableCell>
                ) : null}
                <TableCell>
                  <Row spacing="none" alignmentVertical="center">
                    {isActiveEntry(entry.startTime) ? (
                      <Text type="b200">{entry.startTime ? getDateInUTC(entry.startTime) : '—'}</Text>
                    ) : (
                      <DatePicker
                        value={timestampToDateStr(entry.startTime)}
                        onChange={(value: string) => handleStartDateChange(entry.entryId, value)}
                        size="small"
                        disabledDates={isBeforeDate(getEarliestStartDateStr(macroplanogramStartTime))}
                      />
                    )}
                    {(() => {
                      const original = originalEntries.find((o) => o.entryId === entry.entryId);
                      return original && entry.startTime !== original.startTime && !isActiveEntry(entry.startTime) ? (
                        <Button
                          type="icon"
                          size="small"
                          onClick={() =>
                            setEditableEntries((prev) =>
                              prev.map((e) =>
                                e.entryId === entry.entryId ? { ...e, startTime: original.startTime } : e,
                              ),
                            )
                          }
                          aria-label="Reset start date"
                        >
                          <Icon tokens={closeKnockoutTokens} />
                        </Button>
                      ) : null;
                    })()}
                  </Row>
                </TableCell>
                <TableCell>
                  <Row spacing="none" alignmentVertical="center">
                    <DatePicker
                      value={isIndefinite(entry.endTime) ? '' : timestampToDateStr(entry.endTime)}
                      onChange={(value: string) => handleEndDateChange(entry.entryId, value)}
                      size="small"
                      placeholder={isIndefinite(entry.endTime) ? 'Indefinite' : undefined}
                      disabledDates={isOutsideDateRange(
                        isActiveEntry(entry.startTime) ? getTodayStr() : timestampToDateStr(entry.startTime),
                        macroplanogramEndTime,
                      )}
                    />
                    {(() => {
                      const original = originalEntries.find((o) => o.entryId === entry.entryId);
                      return original && entry.endTime !== original.endTime ? (
                        <Button
                          type="icon"
                          size="small"
                          onClick={() =>
                            setEditableEntries((prev) =>
                              prev.map((e) => (e.entryId === entry.entryId ? { ...e, endTime: original.endTime } : e)),
                            )
                          }
                          aria-label="Reset end date"
                        >
                          <Icon tokens={closeKnockoutTokens} />
                        </Button>
                      ) : null;
                    })()}
                  </Row>
                </TableCell>
                <TableCell>
                  <Button
                    type="icon"
                    onClick={() => handleRemoveEntry(entry.entryId)}
                    aria-label={`Remove ${entry.itemId}`}
                  >
                    <Icon tokens={closeKnockoutTokens} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </Table>
        )}

        {/* Add New Items section */}
        {!showAddSection ? (
          <Row alignmentHorizontal="start">
            <Button type="tertiary" size="small" onClick={() => setShowAddSection(true)}>
              Add new items
            </Button>
          </Row>
        ) : (
          <Column spacing="300">
            <Row alignmentVertical="center">
              <Text type="h100">Add New Items</Text>
              <Button
                type="tertiary"
                size="small"
                onClick={() => {
                  setShowAddSection(false);
                  setNewItems([]);
                }}
              >
                Hide
              </Button>
            </Row>
            <AddItemInput
              storeId={storeId}
              macroplanogramId={macroplanogramId}
              selectedDate={selectedDate}
              macroplanogramStartTime={macroplanogramStartTime}
              macroplanogramEndTime={macroplanogramEndTime}
              subcategories={subcategories}
              existingItems={existingItems}
              stagedItems={newItems}
              onStagedItemsChange={setNewItems}
              saveErrors={saveErrors}
            />
          </Column>
        )}
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          {!saveEnabled ? (
            <Tooltip
              position="top"
              title={newItemsMissingSubcategory ? 'Select subcategories for new items' : 'Make changes to save'}
            >
              <span tabIndex={0}>
                <Button type="primary" disabled>
                  Save Changes
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button type="primary" onClick={handleSave}>
              {isSaving ? <Loader type="circular" size="small" /> : 'Save Changes'}
            </Button>
          )}
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default ModifyItemsModal;
